import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  Search,
  Pencil,
  Mail,
  Phone,
  Loader2,
  AlertCircle,
  UserPlus,
  Building2,
  Globe,
  User,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useRecipients, usePlaces, flattenPlaces } from '@/api';
import { AddLeadDrawer } from '@/components/outreach/AddLeadDrawer';
import { PlaceEditDrawer } from '@/components/outreach/PlaceEditDrawer';
import { parseDisplayName } from '@/lib/utils';
import type { Person, Lead } from '@/types/leads';
import styles from './LeadsManage.module.css';

type Tab = 'contacts' | 'businesses';
type StatusFilter = 'all' | 'manual' | 'scraped' | 'imported';

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => {
    timerRef.current = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timerRef.current);
  }, [value, delay]);
  return debounced;
}

export function LeadsManage() {
  const [tab, setTab] = useState<Tab>('contacts');
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState<StatusFilter>('all');
  const debouncedSearch = useDebounce(search, 300);

  const [editingPerson, setEditingPerson] = useState<Person | null>(null);
  const [editingPlace, setEditingPlace] = useState<Lead | null>(null);
  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false);

  // Fetch contacts. 'email' mode = has_email so we see everyone with an email,
  // which covers the bulk of data the user wants to clean up.
  const { data, isLoading, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useRecipients({
      mode: 'email',
      search: debouncedSearch,
    });

  // Fetch places (businesses). Backend search covers displayName, address,
  // phone, and website.
  const {
    data: placesData,
    isLoading: isLoadingPlaces,
    fetchNextPage: fetchNextPlaces,
    hasNextPage: hasNextPlaces,
    isFetchingNextPage: isFetchingNextPlaces,
  } = usePlaces({ search: debouncedSearch });

  const allPersons = useMemo(() => data?.pages.flatMap((p) => p.results) ?? [], [data]);
  const totalCount = data?.pages?.[0]?.count ?? 0;

  const allPlaces = useMemo(() => flattenPlaces(placesData), [placesData]);
  const totalPlaces = placesData?.pages?.[0]?.count ?? 0;

  // Client-side source filter on top of server-side search
  const persons = useMemo(() => {
    if (sourceFilter === 'all') return allPersons;
    const target =
      sourceFilter === 'manual' ? 'MANUAL' : sourceFilter === 'imported' ? 'IMPORT' : 'SCRAPE';
    return allPersons.filter((p) => (p.source || 'SCRAPE') === target);
  }, [allPersons, sourceFilter]);

  const handleEditClick = useCallback((person: Person) => {
    setEditingPerson(person);
  }, []);

  const handleEditClose = useCallback(() => {
    setEditingPerson(null);
  }, []);

  const handleEditSuccess = useCallback(() => {
    // useUpdatePerson already invalidates ['recipients'] — list will refetch
    toast.success('Contact saved');
  }, []);

  const handleAddSuccess = useCallback(() => {
    toast.success('Contact added');
  }, []);

  const handleEditPlace = useCallback((place: Lead) => {
    setEditingPlace(place);
  }, []);

  const handleEditPlaceClose = useCallback(() => {
    setEditingPlace(null);
  }, []);

  const isFilterLoading = !isLoading && isFetching && !isFetchingNextPage;

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Manage leads</h1>
          <p className={styles.pageSubtitle}>
            Fix names, emails, or phones on any contact. Changes are saved immediately and visible
            in every campaign view.
          </p>
        </div>
        <button type="button" className={styles.addBtn} onClick={() => setIsAddDrawerOpen(true)}>
          <UserPlus className={styles.addBtnIcon} />
          Add contacts
        </button>
      </header>

      <div className={styles.topTabs} role="tablist" aria-label="Section">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'contacts'}
          className={`${styles.topTab} ${tab === 'contacts' ? styles.topTabActive : ''}`}
          onClick={() => {
            setTab('contacts');
            setSearch('');
          }}
        >
          <User className={styles.topTabIcon} />
          Contacts
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'businesses'}
          className={`${styles.topTab} ${tab === 'businesses' ? styles.topTabActive : ''}`}
          onClick={() => {
            setTab('businesses');
            setSearch('');
          }}
        >
          <Building2 className={styles.topTabIcon} />
          Businesses
        </button>
      </div>

      <div className={styles.toolbar}>
        <div className={styles.searchWrapper}>
          <Search className={styles.searchIcon} />
          <input
            type="text"
            placeholder={
              tab === 'contacts'
                ? 'Search name, email, phone, or business...'
                : 'Search business name, address, phone, or website...'
            }
            className={styles.searchInput}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {tab === 'contacts' && (
          <div className={styles.filterGroup} role="tablist" aria-label="Source filter">
            {(['all', 'manual', 'scraped', 'imported'] as const).map((key) => {
              const label =
                key === 'all'
                  ? 'All'
                  : key === 'manual'
                    ? 'Manual'
                    : key === 'scraped'
                      ? 'Scraped'
                      : 'Imported';
              return (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={sourceFilter === key}
                  className={`${styles.filterBtn} ${sourceFilter === key ? styles.filterBtnActive : ''}`}
                  onClick={() => setSourceFilter(key)}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}

        <div className={styles.countPill}>
          {tab === 'contacts'
            ? `${persons.length} of ${totalCount}`
            : `${allPlaces.length} of ${totalPlaces}`}
        </div>
      </div>

      {tab === 'contacts' && (
        <div className={styles.listWrap}>
          {isLoading ? (
            <div className={styles.loading}>
              <Loader2 className={styles.loadingIcon} />
              <span>Loading contacts...</span>
            </div>
          ) : persons.length === 0 ? (
            <div className={styles.empty}>
              <AlertCircle className={styles.emptyIcon} />
              <p className={styles.emptyText}>
                {debouncedSearch
                  ? `No contacts match "${debouncedSearch}".`
                  : 'No contacts yet. Use "Add contacts" to paste or create one.'}
              </p>
            </div>
          ) : (
            <>
              {isFilterLoading && (
                <div className={styles.inlineLoading}>
                  <Loader2 className={styles.inlineLoadingIcon} />
                  Updating...
                </div>
              )}
              <ul className={styles.rowList}>
                {persons.map((p) => {
                  const sourceLabel =
                    p.source === 'MANUAL'
                      ? 'Manual'
                      : p.source === 'IMPORT'
                        ? 'Imported'
                        : 'Scraped';
                  const sourceClass =
                    p.source === 'MANUAL'
                      ? styles.sourceBadgeManual
                      : p.source === 'IMPORT'
                        ? styles.sourceBadgeImport
                        : styles.sourceBadgeScrape;
                  return (
                    <li key={p.id} className={styles.row}>
                      <div className={styles.rowIcon}>
                        <User className={styles.rowIconSvg} />
                      </div>
                      <div className={styles.rowBody}>
                        <div className={styles.rowNameLine}>
                          <span className={styles.rowName}>{p.fullName?.trim() || 'No name'}</span>
                          <span className={`${styles.sourceBadge} ${sourceClass}`}>
                            {sourceLabel}
                          </span>
                          {(p.emailBlacklisted ||
                            p.emailBounced ||
                            p.emailSpamReported ||
                            p.emailUnsubscribed) && (
                            <span className={styles.suppressedBadge} title="Email suppressed">
                              suppressed
                            </span>
                          )}
                        </div>
                        <div className={styles.rowMeta}>
                          {p.email && (
                            <span className={styles.metaItem}>
                              <Mail className={styles.metaIcon} />
                              {p.email}
                            </span>
                          )}
                          {p.phoneNumber && (
                            <span className={styles.metaItem}>
                              <Phone className={styles.metaIcon} />
                              {p.phoneNumber}
                            </span>
                          )}
                          {p.place && (
                            <span className={styles.metaItem}>
                              <Building2 className={styles.metaIcon} />
                              {(() => {
                                try {
                                  const parsed = JSON.parse(p.place.displayName || '{}');
                                  return parsed.text || p.place.displayName;
                                } catch {
                                  return p.place.displayName;
                                }
                              })()}
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        className={styles.editBtn}
                        aria-label={`Edit ${p.fullName || 'contact'}`}
                        onClick={() => handleEditClick(p)}
                      >
                        <Pencil className={styles.editBtnIcon} />
                        Edit
                      </button>
                    </li>
                  );
                })}
              </ul>
              {hasNextPage && (
                <button
                  type="button"
                  className={styles.loadMoreBtn}
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                >
                  {isFetchingNextPage ? (
                    <>
                      <Loader2 className={styles.loadMoreIcon} />
                      Loading more...
                    </>
                  ) : (
                    `Load more (${totalCount - allPersons.length} remaining)`
                  )}
                </button>
              )}
            </>
          )}
        </div>
      )}

      {tab === 'businesses' && (
        <div className={styles.listWrap}>
          {isLoadingPlaces ? (
            <div className={styles.loading}>
              <Loader2 className={styles.loadingIcon} />
              <span>Loading businesses...</span>
            </div>
          ) : allPlaces.length === 0 ? (
            <div className={styles.empty}>
              <AlertCircle className={styles.emptyIcon} />
              <p className={styles.emptyText}>
                {debouncedSearch
                  ? `No businesses match "${debouncedSearch}".`
                  : 'No businesses yet.'}
              </p>
            </div>
          ) : (
            <>
              <ul className={styles.rowList}>
                {allPlaces.map((b) => {
                  const name = parseDisplayName(b.displayName)?.text || 'Unknown business';
                  const personCount = b.persons?.length ?? 0;
                  return (
                    <li key={b.id} className={styles.row}>
                      <div className={styles.rowIcon}>
                        <Building2 className={styles.rowIconSvg} />
                      </div>
                      <div className={styles.rowBody}>
                        <div className={styles.rowNameLine}>
                          <span className={styles.rowName}>{name}</span>
                          {personCount > 0 && (
                            <span className={`${styles.sourceBadge} ${styles.sourceBadgeScrape}`}>
                              {personCount} contact{personCount === 1 ? '' : 's'}
                            </span>
                          )}
                        </div>
                        <div className={styles.rowMeta}>
                          {b.formattedAddress && (
                            <span className={styles.metaItem}>
                              <Building2 className={styles.metaIcon} />
                              {b.formattedAddress}
                            </span>
                          )}
                          {b.internationalPhoneNumber && (
                            <span className={styles.metaItem}>
                              <Phone className={styles.metaIcon} />
                              {b.internationalPhoneNumber}
                            </span>
                          )}
                          {(b as Lead & { websiteUri?: string | null }).websiteUri && (
                            <span className={styles.metaItem}>
                              <Globe className={styles.metaIcon} />
                              {(b as Lead & { websiteUri?: string | null }).websiteUri}
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        className={styles.editBtn}
                        aria-label={`Edit ${name}`}
                        onClick={() => handleEditPlace(b)}
                      >
                        <Pencil className={styles.editBtnIcon} />
                        Edit
                      </button>
                    </li>
                  );
                })}
              </ul>
              {hasNextPlaces && (
                <button
                  type="button"
                  className={styles.loadMoreBtn}
                  onClick={() => fetchNextPlaces()}
                  disabled={isFetchingNextPlaces}
                >
                  {isFetchingNextPlaces ? (
                    <>
                      <Loader2 className={styles.loadMoreIcon} />
                      Loading more...
                    </>
                  ) : (
                    `Load more (${totalPlaces - allPlaces.length} remaining)`
                  )}
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* Edit drawer — reuses AddLeadDrawer in edit mode */}
      <AddLeadDrawer
        isOpen={editingPerson !== null}
        onClose={handleEditClose}
        onSuccess={handleEditSuccess}
        channel="EMAIL"
        editingPerson={editingPerson}
      />

      {/* Add new contact drawer */}
      <AddLeadDrawer
        isOpen={isAddDrawerOpen}
        onClose={() => setIsAddDrawerOpen(false)}
        onSuccess={handleAddSuccess}
        channel="EMAIL"
      />

      {/* Place edit drawer */}
      <PlaceEditDrawer
        isOpen={editingPlace !== null}
        place={editingPlace}
        onClose={handleEditPlaceClose}
      />
    </div>
  );
}
